import { Component, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  chevronDownOutline,
  chevronForwardOutline,
  copyOutline,
  eyeOffOutline,
  eyeOutline,
  lockClosedOutline,
  lockOpenOutline,
  trashOutline
} from 'ionicons/icons';
import { StudioColorInputComponent } from '../studio-color-input/studio-color-input.component';
import { InfoTooltipComponent } from '../info-tooltip/info-tooltip.component';
import { STUDIO_COLOR_FIELD_CONFIGS } from '../studio-color-input/studio-color-input.config';
import { StudioMeasureInputComponent } from '../studio-measure-input/studio-measure-input.component';
import { StudioSpaceInputComponent } from '../studio-space-input/studio-space-input.component';
import { STUDIO_MEASURE_FIELD_CONFIGS } from '../studio-measure-input/studio-measure-input.config';
import { ComponentStudioService } from '../../services/component-studio.service';
import {
  StudioAnimationName,
  StudioNode,
  StudioNodeAnimation,
  StudioNodeContent,
  StudioNodeStyle
} from '../../models/component-studio.models';

@Component({
  selector: 'app-component-builder-inspector',
  standalone: true,
  imports: [FormsModule, IonIcon, InfoTooltipComponent, StudioColorInputComponent, StudioMeasureInputComponent, StudioSpaceInputComponent],
  templateUrl: './component-builder-inspector.component.html',
  styleUrls: ['./component-builder-inspector.component.scss']
})
export class ComponentBuilderInspectorComponent {
  readonly studioService = this.componentStudioService;
  readonly displayOptions = ['flex', 'block', 'grid', 'inline-flex', 'inline-block', 'inline', 'none'];
  readonly flexDirectionOptions = ['row', 'column', 'row-reverse', 'column-reverse'];
  readonly flexWrapOptions = ['nowrap', 'wrap', 'wrap-reverse'];
  readonly justifyOptions = ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly'];
  readonly alignOptions = ['stretch', 'flex-start', 'center', 'flex-end', 'baseline'];
  readonly alignSelfOptions = ['auto', 'stretch', 'flex-start', 'center', 'flex-end', 'baseline'];
  readonly textAlignOptions = ['left', 'center', 'right', 'justify'];
  readonly fontStyleOptions = ['normal', 'italic', 'oblique'];
  readonly textTransformOptions = ['none', 'uppercase', 'lowercase', 'capitalize'];
  readonly textDecorationOptions = ['none', 'underline', 'line-through', 'overline'];
  readonly whiteSpaceOptions = ['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'];
  readonly wordBreakOptions = ['normal', 'break-word', 'break-all', 'keep-all'];
  readonly borderStyleOptions = ['none', 'solid', 'dashed', 'dotted', 'double'];
  readonly backgroundRepeatOptions = ['repeat', 'no-repeat', 'repeat-x', 'repeat-y', 'round', 'space'];
  readonly overflowOptions = ['visible', 'hidden', 'auto', 'scroll', 'clip'];
  readonly visibilityOptions = ['visible', 'hidden'];
  readonly pointerEventsOptions = ['auto', 'none'];
  readonly cursorOptions = ['auto', 'pointer', 'text', 'move', 'grab', 'not-allowed', 'help', 'crosshair'];
  readonly objectFitOptions = ['cover', 'contain', 'scale-down', 'none'];
  readonly positionOptions = ['static', 'relative', 'absolute', 'sticky', 'fixed'] as const;
  readonly animationOptions: StudioAnimationName[] = ['none', 'fade-up', 'fade-in', 'pop-in', 'pulse', 'float'];
  readonly easingOptions = ['ease', 'ease-in', 'ease-out', 'ease-in-out', 'linear'];
  readonly fontWeightOptions = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];
  readonly sectionOrder = ['selected', 'content', 'layout', 'surface', 'transform', 'animation', 'advanced'] as const;
  readonly colorFieldConfigs = STUDIO_COLOR_FIELD_CONFIGS;
  readonly measureFieldConfigs = STUDIO_MEASURE_FIELD_CONFIGS;
  private readonly openSectionStorageKey = 'bw.component-builder.inspector.open-section';
  private openSectionId: (typeof this.sectionOrder)[number] | null = this.restoreOpenSection();

  constructor(private readonly componentStudioService: ComponentStudioService) {
    addIcons({
      chevronDownOutline,
      chevronForwardOutline,
      copyOutline,
      eyeOffOutline,
      eyeOutline,
      lockClosedOutline,
      lockOpenOutline,
      trashOutline
    });

    effect(() => {
      const selectedNodeId = this.componentStudioService.selectedNodeId();
      if (!selectedNodeId) {
        return;
      }

      if (!this.openSectionId || !this.sectionOrder.includes(this.openSectionId)) {
        this.setOpenSection('selected');
      }
    });
  }

  updateLabel(value: string): void {
    this.componentStudioService.updateSelectedNodeLabel(value);
  }

  updateContent(field: keyof StudioNodeContent, value: string): void {
    this.componentStudioService.updateSelectedNodeContent({ [field]: value } as Partial<StudioNodeContent>);
  }

  updateStyle(field: keyof StudioNodeStyle, value: string): void {
    this.componentStudioService.updateSelectedNodeStyle({ [field]: value } as Partial<StudioNodeStyle>);
  }

  updateNumericStyle(field: keyof StudioNodeStyle, value: string): void {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    this.componentStudioService.updateSelectedNodeStyle({ [field]: parsedValue } as Partial<StudioNodeStyle>);
  }

  updateTransform(field: 'translateX' | 'translateY' | 'scaleX' | 'scaleY' | 'rotate' | 'skewX' | 'skewY', value: string): void {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    this.componentStudioService.updateSelectedNodeTransform({ [field]: parsedValue });
  }

  updateAnimationNumber(field: 'durationMs' | 'delayMs', value: string): void {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    this.componentStudioService.updateSelectedNodeAnimation({ [field]: parsedValue });
  }

  updateAnimationText(field: 'name' | 'easing' | 'iterationCount', value: string): void {
    this.componentStudioService.updateSelectedNodeAnimation({ [field]: value } as Partial<StudioNodeAnimation>);
  }

  toggleHidden(): void {
    this.componentStudioService.toggleSelectedHidden();
  }

  toggleLocked(): void {
    this.componentStudioService.toggleSelectedLocked();
  }

  duplicateSelected(): void {
    this.componentStudioService.duplicateSelectedNode();
  }

  removeSelected(): void {
    this.componentStudioService.removeSelectedNode();
  }

  canRemoveSelected(node: StudioNode | null): boolean {
    return !!node && !!this.componentStudioService.selectedParentNode();
  }

  isSectionOpen(sectionId: (typeof this.sectionOrder)[number]): boolean {
    return this.openSectionId === sectionId;
  }

  toggleSection(sectionId: (typeof this.sectionOrder)[number]): void {
    this.setOpenSection(this.openSectionId === sectionId ? null : sectionId);
  }

  private setOpenSection(sectionId: (typeof this.sectionOrder)[number] | null): void {
    this.openSectionId = sectionId;

    if (typeof window === 'undefined') {
      return;
    }

    if (!sectionId) {
      window.localStorage.removeItem(this.openSectionStorageKey);
      return;
    }

    window.localStorage.setItem(this.openSectionStorageKey, sectionId);
  }

  private restoreOpenSection(): (typeof this.sectionOrder)[number] | null {
    if (typeof window === 'undefined') {
      return 'selected';
    }

    const storedValue = window.localStorage.getItem(this.openSectionStorageKey);
    if (storedValue && this.sectionOrder.includes(storedValue as (typeof this.sectionOrder)[number])) {
      return storedValue as (typeof this.sectionOrder)[number];
    }

    return 'selected';
  }
}
